import { Link } from "react-router-dom";
import { usePublicStats } from "@/api/hooks";
import edgeCareLogo from "@/assets/logo.png";

function MetricValue({ value, loading }: { value?: number; loading: boolean }) {
  if (loading) {
    return (
      <span className="inline-block h-[0.85em] w-10 animate-pulse rounded-sm bg-white/20 align-baseline" />
    );
  }

  return <>{(value ?? 0).toLocaleString()}</>;
}

export default function LandingPage() {
  const stats = usePublicStats();
  const data = stats.data?.data;

  return (
    <main className="min-h-screen bg-[#181818] px-10 py-7 text-white max-sm:px-5 max-sm:py-5">
      <div className="mx-auto flex min-h-[calc(100vh-3.5rem)] max-w-[1120px] flex-col max-sm:min-h-[calc(100vh-2.5rem)]">
        <header className="flex justify-end">
          <Link
            to="/login/sign-in"
            className="text-base underline underline-offset-2 transition-opacity hover:opacity-75 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#75ebfc]"
          >
            login
          </Link>
        </header>

        <section className="flex flex-1 flex-col justify-center py-8 max-md:py-10">
          <div className="grid w-full grid-cols-[320px_minmax(0,1fr)] items-center gap-12 max-md:grid-cols-1 max-md:gap-10">
            <img
              src={edgeCareLogo}
              alt="EdgeCare — Leading-edge Research for Better Care"
              className="aspect-square w-[320px] object-contain max-md:mx-auto max-md:w-[280px] max-sm:w-60"
            />

            <p className="text-[clamp(1.8rem,3.1vw,2.5rem)] font-normal leading-[1.22] tracking-[-0.025em] max-md:text-center">
              The <strong className="font-bold text-[#75ebfc]">EdgeCare dataset</strong> is a first-of-its-kind{" "}
              <strong className="font-bold text-[#75ebfc]">longitudinal, multimodal resource</strong> combining passive smartphone sensing and EMA data from midlife and older adults living alone, enabling research on{" "}
              <strong className="font-bold text-[#75ebfc]">social health</strong> and{" "}
              <strong className="font-bold text-[#75ebfc]">healthy aging</strong>.
            </p>
          </div>

          <p
            className="mt-16 w-full text-center text-[clamp(1.15rem,2vw,1.65rem)] leading-snug tracking-[-0.015em]"
            aria-live="polite"
          >
            <strong className="font-bold text-[#75ebfc]">
              <MetricValue value={data?.total_participants} loading={stats.isLoading} />
            </strong>{" "}
            participants <span className="mx-2" aria-hidden="true">•</span>{" "}
            <strong className="font-bold text-[#75ebfc]">
              <MetricValue value={data?.observation_days} loading={stats.isLoading} />
            </strong>{" "}
            days of observation <span className="mx-2" aria-hidden="true">•</span>{" "}
            <strong className="font-bold text-[#75ebfc]">
              <MetricValue value={data?.total_samples} loading={stats.isLoading} />
            </strong>{" "}
            multimodal data samples collected
          </p>

        </section>

        <footer className="pb-1 text-center">
          <a
            href="https://edgecare.asu.edu"
            target="_blank"
            rel="noreferrer"
            className="text-base underline underline-offset-2 transition-opacity hover:opacity-75 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#75ebfc]"
          >
            about us
          </a>
        </footer>
      </div>
    </main>
  );
}
